/**
 * Utility functions for formatting complexity notation in text.
 *
 * These functions are designed to be used before saving text to the database,
 * ensuring consistent markdown formatting for Big O notation throughout the app.
 */

/**
 * Regex pattern to match Big O notation that is NOT already wrapped in backticks.
 *
 * - (?<!`) - negative lookbehind: ensures not preceded by a backtick
 * - O\( - matches literal "O("
 * - [^)]+ - matches one or more characters that are NOT closing parenthesis
 * - \) - matches literal ")"
 * - (?!`) - negative lookahead: ensures not followed by a backtick
 *
 * Examples matched: O(n), O(n²), O(n^2), O(n log n), O(k * log input)
 * Examples NOT matched: `O(n)` (already wrapped)
 */
const BIG_O_REGEX = /(?<!`)O\([^)]+\)(?!`)/g

/**
 * Wraps Big O notation (e.g., O(n), O(n log n)) in backticks for markdown formatting.
 * Handles cases where the notation is already wrapped to prevent double-wrapping.
 *
 * @param text - The input text that may contain Big O notation
 * @returns The text with all Big O notations wrapped in backticks
 *
 * @example
 * formatBigONotation("O(n)") // Returns "`O(n)`"
 * formatBigONotation("This is O(n) time") // Returns "This is `O(n)` time"
 * formatBigONotation("`O(n)`") // Returns "`O(n)`" (unchanged)
 * formatBigONotation("O(n), not O(n log n).") // Returns "`O(n)`, not `O(n log n)`."
 */
export function formatBigONotation(text: string): string {
  if (!text) return text
  return text.replace(BIG_O_REGEX, (match) => `\`${match}\``)
}
